import {
  Stack, Typography, Paper, Box, Chip, Divider,
} from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { useParams } from "react-router";
import { InfoList, InfoListColDef } from "../../../components/InfoList";
import { searchedMaterial, searchedMaterialRows } from "../../../schema/console/material";
import { DataLink, MasterLink } from "../../../components/links/EntityLinks";
import { IsAnyDeleted } from "../../../util/DataExtensions";
import { MaterialMasterBase } from "../../../schema/generated/MaterialMaster";
import { MaterialDetailBase, MaterialDetailBaseSchema } from "../../../schema/generated/MaterialDetail";
import { WorkDetailBase, WorkDetailBaseSchema } from "../../../schema/generated/WorkDetail";
import { useMaterialMasters } from "../../../util/useMaterialMasters";
import { useIsOnline } from "../../../util/useIsOnline";
import { getRowsChecked } from "../../../util/getRowsChecked";
import { DetailGuard, useDetailFetch, useDetailRetry } from "../../../components/DetailFetch";
import { PageContext } from "../PageContext";
import Time from "../../../util/Time";
import { actorOf, materialStage, remainingRatioOfQr, workLabel } from "./helpers";
import { formatRatioAsBagsKg, getMaterialSetKg } from "../../../util/consumption";
import { errorMessageOf } from "../../../util/typed";

export function MaterialStatusPage({ pageContext, materials }: { pageContext: PageContext, materials?: searchedMaterialRows | null }) {
  const { id } = useParams();
  const { materials: materialMasters } = useMaterialMasters(pageContext.api);
  const [materialDetails, setMaterialDetails] = useState<MaterialDetailBase[]>([]);
  const [workDetails, setWorkDetails] = useState<WorkDetailBase[]>([]);

  // 構成細目(materialDetailsマスタ合算)、工程名(work-details)用。
  const isOnline = useIsOnline(pageContext.api);
  useEffect(() => {
    // オフライン時は取得しない(IDBフォールバックが無くエラーダイアログになるため)。復帰時に取得する。
    if (!isOnline) {
      return;
    }
    let active = true;
    getRowsChecked(pageContext.api, "material-details", MaterialDetailBaseSchema.base)
      .then((rows) => { if (active) { setMaterialDetails(rows); } })
      .catch((e) => {
        console.warn("material-details fetch failed", e);
        if (active) {
          pageContext.api.showError(`材料細目の取得に失敗しました。\n${errorMessageOf(e)}`);
        }
      });
    getRowsChecked(pageContext.api, "work-details", WorkDetailBaseSchema.base)
      .then((rows) => { if (active) { setWorkDetails(rows); } })
      .catch((e) => {
        console.warn("work-details fetch failed", e);
        if (active) {
          pageContext.api.showError(`工程マスタの取得に失敗しました。\n${errorMessageOf(e)}`);
        }
      });
    return () => { active = false; };
  }, [pageContext, isOnline]);

  const [retryCount, retry] = useDetailRetry();
  const state = useDetailFetch<searchedMaterial>(() => {
    const targetId = Number(id);
    if (!targetId) {
      return Promise.resolve(null);
    }
    return Promise.resolve(materials?.find((m) => m.id === targetId))
      .then((cached) => cached ?? pageContext.api.material.getLifeSpanFromId({ id: targetId }));
  }, [pageContext, materials, id, retryCount]);

  return (
    <DetailGuard state={state} notFoundLabel={`材料 ${id}`} onRetry={retry}>
      {(target) => (
        <MaterialStatusView target={target} materialMasters={materialMasters} materialDetails={materialDetails} workDetails={workDetails} />
      )}
    </DetailGuard>
  );
}

// LotNo欄の表示。aliveなロットを優先し、削除済みロットは履歴として注記付きで表示、どちらも無ければ出荷前。
function lotNoContext(activeLot: searchedMaterial["Lot"], deletedHistoryLot: searchedMaterial["Lot"]): ReactNode {
  if (activeLot != null) {
    return activeLot.lot_no ?? "(LotNo未記録)";
  }
  if (deletedHistoryLot != null) {
    return <Typography component="span" color="warning.main">{`${deletedHistoryLot.lot_no ?? "(LotNo未記録)"}（出荷記録は削除済み）`}</Typography>;
  }
  return <Typography component="span" color="error">材料ロット未紐付け（出荷前）</Typography>;
}

function lifeStepSummaryForLot(activeLot: searchedMaterial["Lot"], deletedHistoryLot: searchedMaterial["Lot"], setCapacity: string): string {
  if (activeLot != null) {
    return `/ LotNo ${activeLot.lot_no ?? "-"} / 1セット ${setCapacity || "-"}`;
  }
  if (deletedHistoryLot != null) {
    return `/ LotNo ${deletedHistoryLot.lot_no ?? "-"}（出荷記録は削除済み）`;
  }
  return "";
}

function lifeStepSummaryForCarryIn(activeCarryIn: searchedMaterial["CarryIn"], deletedHistoryCarryIn: searchedMaterial["CarryIn"]): string {
  if (activeCarryIn != null) {
    if (activeCarryIn.carried_in_at != null) {
      return `(${new Time(activeCarryIn.carried_in_at).Format("YYYY/MM/dd HH:mm:ss")})`;
    }
    return "(未受領)";
  }
  if (deletedHistoryCarryIn != null) {
    return "（搬入記録は削除済み）";
  }
  return "";
}

function MaterialStatusView({ target, materialMasters, materialDetails, workDetails }: {
  target: searchedMaterial; materialMasters: MaterialMasterBase[]; materialDetails: MaterialDetailBase[]; workDetails: WorkDetailBase[];
}) {
  const issuance = target.Issuance;
  const activeCarryOut = IsAnyDeleted(target.Lot?.CarryOut) ? null : target.Lot?.CarryOut;
  const activeLot = IsAnyDeleted(target.Lot, activeCarryOut) ? null : target.Lot;
  const activeCarryInWork = IsAnyDeleted(target.CarryIn?.Work) ? null : target.CarryIn?.Work;
  const activeCarryIn = IsAnyDeleted(target.CarryIn, activeCarryInWork) ? null : target.CarryIn;
  // 出荷/搬入の記録が論理削除されていても、QR個票のトレーサビリティ履歴としては表示する(削除済み注記付き)。
  // APIのQR詳細は削除済みの物流親も解決して返す(履歴解決)。
  const deletedHistoryLot = activeLot == null && target.Lot != null ? target.Lot : null;
  const deletedHistoryCarryIn = activeCarryIn == null && target.CarryIn != null ? target.CarryIn : null;
  const activeConstructionPlanWork = IsAnyDeleted(target.Construction?.ConstructionPlan?.Work) ? null : target.Construction?.ConstructionPlan?.Work;
  const activeConstructionPlan = IsAnyDeleted(target.Construction?.ConstructionPlan, activeConstructionPlanWork) ? null : target.Construction?.ConstructionPlan;
  const activeConstruction = IsAnyDeleted(target.Construction, activeConstructionPlan) ? null : target.Construction;

  const matId = activeLot?.material_id ?? undefined;
  const matName = matId != null ? (materialMasters.find((m) => m.id === matId)?.name ?? `#${matId}`) : "-";
  const details = matId != null
    ? materialDetails.filter((d) => d.material_id === matId && d.deleted_at == null).sort((a, b) => a.sort_order - b.sort_order)
    : [];
  const constituents = details.map((d) => d.detail_name).join(" + ");
  const setCapacity = details
    .map((d) => d.content_quantity != null ? `${d.detail_name} ${d.content_quantity}${d.content_unit}` : null)
    .filter(Boolean).join(" + ");

  const workLink = activeCarryInWork
    ? <MasterLink type="work" id={activeCarryInWork.id} name={workLabel(activeCarryInWork)} />
    : null;
  const stage = materialStage(target);

  // 消費履歴(部分消費対応): このQRに紐づく alive な消費明細(施工×バッチ)を古い順に表示する。
  // バッチ/工程は材料検索応答の消費行に識別子が無いため、削除済み判定の対象外とする。
  const consumptions = target.Consumptions;
  const setKg = matId != null ? getMaterialSetKg(materialDetails, matId) : 0;
  const remainingRatio = remainingRatioOfQr(target);


  // 消費行の共通表示(施工リンク+消費量+日時+削除チップ)。InfoList の消費履歴と、
  // ライフサイクル末端「施工消費」の施工ごとブロック内の消費履歴で使う。
  const consumptionRowsNode = (rows: typeof consumptions): ReactNode => (
    <Stack gap={0.5}>
      {rows.map((c, i) => (
        <Stack key={i} direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <DataLink type="construction" id={c.construction_id} />
          <Typography variant="body2" fontWeight={600}>{formatRatioAsBagsKg(c.used_ratio, setKg)}</Typography>
          <Typography variant="caption" color="text.secondary">{new Time(c.createdAt).Format("YYYY/MM/dd HH:mm:ss")}</Typography>
          {c.Construction?.deleted_at != null && <Chip size="small" color="error" label="施工が削除済み" sx={{ height: 22 }} />}
        </Stack>
      ))}
    </Stack>
  );
  const consumptionHistoryNode = consumptions.length === 0 ? null : consumptionRowsNode(consumptions);

  // 施工消費ブロックの構築: 消費行を施工ごとにグループ化し、施工ごとに
  // 「リンク+サマリ / 作業者 / 枠(消費履歴・工程・施工箇所)」のセクションを作る(mock 指定の縦積み表示)。
  const consumptionGroups: { constructionId: string; rows: typeof consumptions }[] = [];
  for (const c of consumptions) {
    const group = consumptionGroups.find((g) => g.constructionId === c.construction_id);
    if (group) {
      group.rows.push(c);
    } else {
      consumptionGroups.push({ constructionId: c.construction_id, rows: [c] });
    }
  }

  const buildConsumptionSection = (group: { constructionId: string; rows: typeof consumptions }): LifeStepSection => {
    const nestedConstruction = group.rows[0]?.Construction ?? null;
    // 工程実施は消費行 nest を優先し、代表施工なら詳細 nest(User 込みの従来応答)へフォールバックする。
    const nestedPEs = nestedConstruction?.ProcessExecutions ?? (group.constructionId === activeConstruction?.id ? activeConstruction?.ProcessExecutions ?? [] : []);
    const groupPEs = (nestedPEs ?? [])
      .filter((p) => p.deleted_at == null)
      .filter((p, index, all) => all.findIndex((x) => x.id === p.id) === index);
    const groupWorkDetails = Array.from(new Set(groupPEs.map((p) => p.work_detail_id)))
      .map((workDetailId) => workDetails.find((w) => w.id === workDetailId))
      .filter((w): w is WorkDetailBase => w != null);
    const groupAreas = groupPEs
      .map((p) => p.ConstructionPlanArea)
      .filter((a): a is NonNullable<typeof a> => a != null && a.deleted_at == null)
      .filter((a, index, all) => all.findIndex((x) => x.id === a.id) === index);
    const groupTotalRatio = group.rows.reduce((sum, c) => sum + c.used_ratio, 0);
    const groupUser = group.constructionId === activeConstruction?.id ? activeConstruction?.User : nestedConstruction?.User;
    return {
      link: (
        <Stack direction="row" alignItems="center" gap={1}>
          <DataLink type="construction" id={group.constructionId} />
          {nestedConstruction?.deleted_at != null && <Chip size="small" color="error" label="施工が削除済み" sx={{ height: 22 }} />}
        </Stack>
      ),
      summary: `(${group.rows.length} 回消費 / 累計 ${formatRatioAsBagsKg(groupTotalRatio, setKg)}${remainingRatio > 1e-3 ? ` / 残 ${formatRatioAsBagsKg(remainingRatio, setKg)}` : ""})`,
      actor: actorOf(groupUser),
      extra: [
        { label: "消費履歴", node: consumptionRowsNode(group.rows) },
        ...(groupWorkDetails.length > 0 ? [{
          label: "工程",
          node: (
            <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
              {groupWorkDetails.map((w) => (
                <MasterLink key={w.id} type="work-detail" id={w.id} name={w.name} />
              ))}
            </Stack>
          ),
        }] : []),
        ...(groupAreas.length > 0 ? [{
          label: "施工箇所",
          node: (
            <Stack direction="row" gap={0.5} flexWrap="wrap" alignItems="center">
              {groupAreas.map((a) => (
                <Stack key={a.id} direction="row" alignItems="center" gap={0.5}>
                  <DataLink type="plan-area" id={a.id} />
                  {activeCarryInWork && <Typography variant="caption" color="text.secondary">({workLabel(activeCarryInWork)})</Typography>}
                </Stack>
              ))}
            </Stack>
          ),
        }] : []),
      ],
    };
  };

  const consumptionSections = consumptionGroups.map(buildConsumptionSection);
  // 消費行が無い(レガシーの施工記録のみ等)場合は、代表施工1件のセクションへフォールバックする。
  let lifecycleSections = consumptionSections;
  if (lifecycleSections.length === 0 && activeConstruction != null) {
    lifecycleSections = [{
      link: <DataLink type="construction" id={activeConstruction.id} />,
      summary: activeConstruction.executed_at != null ? `(${new Time(activeConstruction.executed_at).Format("YYYY/MM/dd HH:mm:ss")})` : "",
      actor: actorOf(activeConstruction.User),
      extra: null,
    }];
  }

  const infoCols: InfoListColDef[] = [
    { label: "材料ID", context: `材料 ${target.id}` },
    { label: "材料", context: matId != null ? <MasterLink type="material" id={matId} name={matName} /> : "-" },
    { label: "構成細目 (1 セット内訳)", context: constituents || "-" },
    { label: "LotNo", context: lotNoContext(activeLot, deletedHistoryLot) },
    { label: "製造日", context: activeLot?.manufactured_at != null ? new Time(activeLot.manufactured_at).Format("YYYY/MM/dd") : "" },
    { label: "1セット容量", context: setCapacity || "-" },
    { label: "工事", context: workLink ?? <Typography component="span" color="error">工事未登録</Typography> },
    { label: "消費履歴", context: consumptionHistoryNode ?? <Typography component="span" color="error">施工実施 未登録</Typography> },
    { label: "残量", context: formatRatioAsBagsKg(remainingRatio, setKg) },
    { label: "状態", context: <Chip size="small" label={stage.label} color={stage.color} variant="outlined" /> },
  ];

  return (
    <Stack p={3} gap={3} maxWidth={1200} mx="auto">
      <Typography variant="h4" fontWeight="bold">材料 {target.id}</Typography>
      <InfoList cols={infoCols} columns={3} />

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight="bold" mb={2}>ライフサイクル</Typography>
        <LifeStepRow label="発行"
          done={!!issuance}
          link={issuance ? <DataLink type="issuance" id={issuance.id} /> : null}
          summary={issuance ? `(${new Time(issuance.createdAt).Format("YYYY/MM/dd HH:mm:ss")})` : ""}
          actor={actorOf(issuance?.User)}
        />
        <LifeStepRow label="出荷"
          done={!!activeLot || !!deletedHistoryLot}
          link={activeLot && activeCarryOut ? <DataLink type="lot" id={activeLot.id} parentId={activeCarryOut.id} /> : null}
          summary={lifeStepSummaryForLot(activeLot, deletedHistoryLot, setCapacity)}
          actor={actorOf((activeLot ?? deletedHistoryLot)?.User)}
          extra={activeLot ? [
            { label: "材料", node: matId != null ? <MasterLink type="material" id={matId} name={matName} /> : "-" },
            { label: "構成細目", node: constituents || "-" },
            { label: "製造日", node: activeLot.manufactured_at != null ? new Time(activeLot.manufactured_at).Format("YYYY/MM/dd") : "-" },
          ] : null}
        />
        <LifeStepRow label="搬入"
          done={!!activeCarryIn || !!deletedHistoryCarryIn}
          link={activeCarryIn ? <DataLink type="carry-in" id={activeCarryIn.id} /> : null}
          summary={lifeStepSummaryForCarryIn(activeCarryIn, deletedHistoryCarryIn)}
          actor={actorOf((activeCarryIn ?? deletedHistoryCarryIn)?.User)}
          extra={activeCarryIn ? [
            { label: "工事", node: workLink ?? "-" },
            { label: "気温", node: activeCarryIn.temperature != null ? `${activeCarryIn.temperature}℃` : "-" },
            { label: "湿度", node: activeCarryIn.humidity != null ? `${activeCarryIn.humidity}%` : "-" },
          ] : null}
        />
        {/* 末端ステップ = 施工消費(mock 指定の縦積み)。消費先の施工ごとに
            「リンク+サマリ / 作業者 / 枠(消費履歴・工程・施工箇所)」のブロックを縦に並べる。 */}
        <LifeStepRow label="施工消費"
          done={lifecycleSections.length > 0} isLast
          link={null}
          actor={null}
          sections={lifecycleSections}
        />
      </Paper>
    </Stack>
  );
}

// ライフサイクル1ステップ内の表示ブロック(リンク+サマリ行 / 作業者 / 追加情報の枠)。
// 施工消費のように1ステップへ複数ブロックを縦に並べる場合は sections で渡す。
type LifeStepSection = {
  link: ReactNode | null;
  summary?: string;
  actor: { name: string; group_name?: string } | null;
  extra?: { label: string; node: ReactNode }[] | null;
};

// mock の LifeStepRow を移植: 縦タイムライン1行。done で色変化、作業者・extra・summary を表示。
// sections を渡すとブロック(リンク行+作業者+枠)を縦に複数並べる(先頭ブロックのみラベルと同じ行に出す)。
function LifeStepRow({ label, done, summary, actor, link, extra, sections, isLast }: {
  label: string; done: boolean; summary?: string;
  actor: { name: string; group_name?: string } | null;
  link: ReactNode | null;
  extra?: { label: string; node: ReactNode }[] | null;
  sections?: LifeStepSection[];
  isLast?: boolean;
}) {
  const resolvedSections: LifeStepSection[] = sections ?? [{ link, summary, actor, extra }];
  return (
    <Stack direction="row" alignItems="stretch" gap={2}>
      <Stack alignItems="center" sx={{ minWidth: 36 }}>
        <Box sx={{ width: 16, height: 16, borderRadius: "50%", mt: 1, bgcolor: done ? "success.main" : "grey.300", border: "2px solid", borderColor: done ? "success.dark" : "grey.400" }} />
        {!isLast && <Box sx={{ flex: 1, width: 2, bgcolor: done ? "success.main" : "grey.300", mt: 0.5 }} />}
      </Stack>
      <Stack flex={1} pb={isLast ? 0 : 2}>
        {!done && (
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography fontWeight="bold" sx={{ minWidth: 90, color: "text.disabled" }}>{label}</Typography>
            <Typography color="text.secondary">未実施</Typography>
          </Stack>
        )}
        {done && resolvedSections.map((section, sectionIndex) => (
          <Box key={sectionIndex} sx={{ mt: sectionIndex === 0 ? 0 : 2 }}>
            <Stack direction="row" alignItems="center" gap={1}>
              {sectionIndex === 0
                ? <Typography fontWeight="bold" sx={{ minWidth: 90, color: "text.primary" }}>{label}</Typography>
                : <Box sx={{ minWidth: 90 }} />}
              {(section.link != null || section.summary)
                ? <Stack direction="row" alignItems="center" gap={0.5}>{section.link}{section.summary && <Typography variant="body2" color="text.secondary">{section.summary}</Typography>}</Stack>
                : <Typography color="text.secondary">未実施</Typography>}
            </Stack>
            {section.actor && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: "90px", mt: 0.5 }}>
                作業者：{section.actor.name}{section.actor.group_name ? `（${section.actor.group_name}）` : ""}
              </Typography>
            )}
            {section.extra && section.extra.length > 0 && (
              <Box sx={{ ml: "90px", mt: 1, p: 1, border: 1, borderColor: "divider", borderRadius: 1, fontSize: 14 }}>
                {section.extra.map((e, i) => (
                  <Stack key={i} direction="row" gap={1}>
                    <Typography variant="body2" sx={{ minWidth: 90 }}>{e.label}：</Typography>
                    <Typography variant="body2" component="span">{e.node}</Typography>
                  </Stack>
                ))}
              </Box>
            )}
          </Box>
        ))}
        {!isLast && <Divider sx={{ mt: 1 }} />}
      </Stack>
    </Stack>
  );
}
